import CarrierRulesMissing from '../carrier/CarrierRulesMissing';
import { useEffect, useRef, useState } from 'react';
import {
  runDesign, watchDesignJob, cancelDesignJob, loadProject,
  downloadDesignKmz, buildDesignReport, buildLomLopSheets, downloadLomLopXlsx,
  downloadText, buildSmartReport, costingNotices,
} from '../../lib/designApi';
import { toFa } from '../../utils/faFormat';
import { priceProject, money, moneyShort, CURRENCY_FA } from '../../lib/tariffsApi';
import { splitterCounts, dropsMetered } from '../../lib/capacityModel';

// Coarse engine stages -> a Persian label for the progress strip. The worker
// currently reports a single 'running' stage; this map is forward-compatible
// with finer stages without breaking (an unknown stage falls back to «در حال اجرا»).
const STAGE_FA = {
  queued: 'در صف اجرا',
  running: 'در حال اجرا',
  placing: 'جانمایی تجهیزات',
  cabling: 'مسیریابی کابل',
  validating: 'اعتبارسنجی',
  finalizing: 'نهایی‌سازی',
  citywide: 'طراحی کل شهر',
};
const stageLabel = (stage, status) => STAGE_FA[stage] || STAGE_FA[status] || 'در حال اجرا';
const faN = (n) => Number(n || 0).toLocaleString('fa-IR');

const MODES = [
  { v: 'greenfield', label: 'طراحی کامل (هیچ نداریم)' },
  { v: 'olt_given', label: 'سایت داده‌شده' },
  { v: 'olt_odc_given', label: 'سایت + ODC داده‌شده' },
  { v: 'down_to_drop', label: 'سایت + ODC + FAT داده‌شده' },
];

// Classify a failed-run body into a readable Persian message: an HTML page (a
// Cloudflare 504/524 page carries "<html") or a timeout/gateway phrase almost
// always means the single-pass engine ran out of time on a large region, so we say
// that; any other engine error is shown verbatim. Note: we do NOT match the bare
// numbers 504/524 - a real engine message can contain those digits (e.g. "a
// continuous run is 524 m > max shoot", "504 buildings unreachable") and must be
// surfaced verbatim, not hidden behind the timeout message.
export const friendlyRunError = (errText) => {
  const t = String(errText || '');
  const looksHtml = /<!doctype|<html/i.test(t);
  if (t === 'اجرای طراحی ناموفق بود.' || t === 'اجرای طراحی به پایان نرسید (احتمالاً منطقه بزرگ است و زمان اجرا تمام شد). محدودهٔ کوچک‌تری انتخاب کنید.') return t;
  const timeoutish = /job_timeout|time-?out|timed out|gateway|ETIMEDOUT|UND_ERR_HEADERS_TIMEOUT/i.test(t);
  if (looksHtml || timeoutish) {
    return 'اجرای طراحی به پایان نرسید (احتمالاً منطقه بزرگ است و زمان اجرا تمام شد). محدودهٔ کوچک‌تری انتخاب کنید.';
  }
  return t ? `اجرای طراحی ناموفق بود: ${t}` : 'اجرای طراحی ناموفق بود';
};

// Cost line for the panel.
//
// Two very different numbers can appear here, and conflating them was the old
// bug: summary.bom.total_cost is the ENGINE's placeholder arithmetic over six
// hardcoded constants ("tune per project" in params.py) with no currency, while
// summary.costing is the real bill priced against a tariff version. When the
// design has been priced we show that, stamped with the book it came from; when
// it has not, we label the engine figure as the rough estimate it is and offer
// to price it.
function CostBlock({ summary, projectId, onPriced, carrier, canEdit }) {
  const [busy, setBusy] = useState(false);
  const c = summary.costing;
  const [pricingError, setPricingError] = useState(null);

  const price = async () => {
    setBusy(true);
    // Re-use the quantities the planner typed last time, so re-pricing against a
    // new tariff reproduces the same bill of quantities instead of dropping them.
    setPricingError(null);
    try {
      const response = await priceProject(projectId, { manual_qty: c?.manual_qty || {} });
      if (response.error || !response.costing) {
        setPricingError(response);
        return;
      }
      onPriced(response.costing);
    } catch (error) {
      if (error.name !== 'AbortError') setPricingError({ error: 'قیمت‌گذاری ناموفق بود.' });
    } finally { setBusy(false); }

  };

  const missing = pricingError?.httpStatus === 412 || pricingError?.code === 'CARRIER_RULES_MISSING';
  const notice = missing ? <CarrierRulesMissing domain="tariffs" carrier={carrier} canEdit={canEdit} variant="dark" />
    : pricingError ? <p role="alert" style={{ color: '#fca5a5' }}>{pricingError.error || 'قیمت‌گذاری ناموفق بود.'}</p> : null;

  if (!c) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 4 }}>
        <div style={{ color: '#fcd34d' }}>
          برآورد اولیهٔ موتور: {faN(Math.round(summary.bom.total_cost || 0))}
          <span style={{ color: '#94a3b8' }}> (بدون واحد پول - فقط برای مقایسهٔ نسبی)</span>
        </div>
        {!missing && <button type="button" onClick={price} disabled={busy} style={{ padding: '7px 12px', borderRadius: 8,
          fontWeight: 700, fontSize: 11.5, cursor: busy ? 'wait' : 'pointer', color: '#0b1220',
          background: 'linear-gradient(135deg,#fbbf24,#d97706)', border: 'none' }}>
          {busy ? 'در حال قیمت‌گذاری…' : `قیمت‌گذاری با فهرست بهای ${carrier?.brand_name || 'اپراتور فعال'}`}
        </button>}
        {notice}
      </div>
    );
  }

  const unit = CURRENCY_FA[c.currency] || '';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 4 }}>
      <div style={{ color: '#5eead4', fontWeight: 700, fontSize: 12.5 }}>
        هزینه‌ی کل: {moneyShort(c.total, c.currency)}
      </div>
      <div>سرانهٔ هر واحد: {money(c.cost_per_premise)} {unit} · {c.lines.length} ردیف</div>
      <div style={{ color: '#cbd5e1' }}>بر پایهٔ «{toFa(c.version_name)}»</div>
      {costingNotices(c, carrier?.id).map((note) => <p key={note} role="status" style={{ color: '#fcd34d' }}>{note}</p>)}
      {!c.complete && (
        <div style={{ color: '#fca5a5' }}>
          ⚠ {c.negotiable.length} قلم «توافقی» قیمت ندارد - این عدد کفِ هزینه است، نه هزینه‌ی نهایی.
        </div>
      )}
      {c.missing_paths && c.missing_paths.length > 0 && (
        <div style={{ color: '#fcd34d' }}>
          ⚠ {c.missing_paths.length} منبع مقدار در خروجی این طرح وجود ندارد؛ طرح را دوباره اجرا کنید.
          {c.missing_paths.includes('counts.site_billable') && ' هزینه‌ی کابینت سایت‌ها محاسبه نشده است.'}
        </div>
      )}
      {!missing && <button type="button" onClick={price} disabled={busy} style={{ padding: '6px 10px', borderRadius: 8,
        fontWeight: 700, fontSize: 11, cursor: busy ? 'wait' : 'pointer', color: '#e2e8f0', marginTop: 2,
        background: 'rgba(148,163,184,0.16)', border: '1px solid rgba(148,163,184,0.35)' }}>
        {busy ? 'در حال قیمت‌گذاری…' : `قیمت‌گذاری مجدد با فهرست بهای ${carrier?.brand_name || 'اپراتور فعال'}`}
      </button>}
        {notice}
    </div>
  );
}

// Runs the engine for `projectId` and reports the summary + violations.
export function DesignPanel({ projectId, projectName, profileName, profileVersionNo, carrier, canEdit, mode, onModeChange, onResult, onBusyChange }) {
  // the KMZ/DDR carry the operator's project name (this is a generic design system
  // run on any operator-selected region, not a fixed site); neutral fallback.
  const exportName = projectName || 'FTTH';
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const brand = { name: carrier?.brand_name, carrierId: carrier?.id, profileLabel: profileName
    ? `${profileName}${profileVersionNo ? ` (نسخهٔ ${toFa(profileVersionNo)})` : ''}` : undefined,
    tariffLabel: summary?.costing?.version_name };
  const [errors, setErrors] = useState([]);
  const [lastResult, setLastResult] = useState(null);
  const [reloadJob, setReloadJob] = useState(null);
  const [smartReport, setSmartReport] = useState(null);
  const [reportBusy, setReportBusy] = useState(false);
  const [lopExportBusy, setLopExportBusy] = useState(false);
  const [progress, setProgress] = useState(null);   // {stage,status,done,total,detail} while a job runs
  const [activeJobId, setActiveJobId] = useState(null);
  const watchRef = useRef(null);                     // the live watchDesignJob handle ({stop})
  // onBusyChange via a ref so the unmount/project-switch effects stay stable even
  // when the parent passes a fresh callback identity every render.
  const onBusyChangeRef = useRef(onBusyChange);
  onBusyChangeRef.current = onBusyChange;

  // Tear down any live watcher on unmount so the poll interval + realtime
  // channel never outlive the panel - and release the parent's busy spinner,
  // which would otherwise stay stuck when the panel unmounts mid-run.
  useEffect(() => () => {
    if (watchRef.current) { watchRef.current.stop(); watchRef.current = null; }
    if (onBusyChangeRef.current) onBusyChangeRef.current(false);
  }, []);

  // Re-key the watcher on project switch: onJobUpdate closes over the projectId
  // captured at run() time, so a long job finishing after the operator switched
  // projects would apply project A's geometry/summary while project B is open.
  // When projectId CHANGES (guarded against the initial mount), stop the watch
  // and clear every per-project piece of UI state.
  const prevProjectRef = useRef(projectId);
  useEffect(() => {
    if (prevProjectRef.current === projectId) return;
    prevProjectRef.current = projectId;
    if (watchRef.current) { watchRef.current.stop(); watchRef.current = null; }
    setActiveJobId(null);
    setProgress(null);
    setBusy(false);
    setSummary(null);
    setLastResult(null);
    setReloadJob(null);
    setErrors([]);
    setSmartReport(null);
    if (onBusyChangeRef.current) onBusyChangeRef.current(false);
  }, [projectId]);

  // Stop watching + clear the busy/progress UI. Idempotent.
  const finishWatch = () => {
    if (watchRef.current) { watchRef.current.stop(); watchRef.current = null; }
    setActiveJobId(null);
    setProgress(null);
    setBusy(false); if (onBusyChange) onBusyChange(false);
  };

  const genReport = async () => {
    setReportBusy(true);
    const { report, error } = await buildSmartReport(projectId);
    setReportBusy(false);
    if (error || !report) { window.alert(error || 'گزارش تولید نشد'); return; }
    setSmartReport(report);
  };

  const exportLomLop = async () => {
    setLopExportBusy(true);
    try {
      const filename = `${exportName}-LOM-LOP.xlsx`;
      const { error } = await downloadLomLopXlsx(
        filename,
        buildLomLopSheets(lastResult, exportName, brand),
      );
      if (error) window.alert(error);
    } catch (e) {
      window.alert((e && e.message) || 'ساخت فایل اکسل ناموفق بود');
    } finally {
      setLopExportBusy(false);
    }
  };

  // Drive the panel off the async job lifecycle. A 'running' (or 'queued')
  // update advances the Persian progress strip; 'complete' loads the persisted
  // geometry (the worker saved it via the internal callback) and surfaces the
  // result; 'failed' alerts the friendly message; any terminal status clears busy.
  const onJobUpdate = async (j) => {
    if (!j) return;
    if (j.status === 'queued' || j.status === 'running') {
      setProgress({ stage: j.stage, status: j.status, done: j.progress_done || 0, total: j.progress_total || 0, detail: j.stage_detail || null });
      return;
    }
    if (j.status === 'complete') {
      // The geometry was persisted server-side by the worker callback; pull it
      // and rebuild the in-memory result the rest of the UI (KMZ/DDR/map) reads.
      try {
        const { elements, routes, error } = await loadProject(projectId);
        if (error) throw error;
        const result = { elements: elements || [], routes: routes || [], summary: j.result_summary || {} };
        setSummary(result.summary);
        setLastResult(result);
        setReloadJob(null);
        setErrors((result.summary.violations || []).filter((v) => v.severity === 'error'));
        if (onResult) onResult(result);
      } catch {
        setReloadJob(j);
      } finally { finishWatch(); }
      return;
    }
    if (j.status === 'failed') {
      window.alert(friendlyRunError(j.error));
      finishWatch();
      return;
    }
    if (j.status === 'cancelled') {
      finishWatch();
      return;
    }
    // Unknown/partial status (e.g. a malformed realtime row, or a transitional
    // server status this client predates): IGNORE it - do NOT stop the watch or
    // clear the progress UI; the next poll carries the authoritative state.
  };

  const run = async () => {
    if (!projectId) { window.alert('ابتدا یک پروژه‌ی طراحی بساز'); return; }
    // a second click while a watcher is live must not spawn a parallel watcher
    if (watchRef.current) return;
    setBusy(true); if (onBusyChange) onBusyChange(true); setErrors([]);
    setProgress({ stage: 'queued', status: 'queued', done: 0, total: 0 });
    try {
      const { job_id: jobId, error } = await runDesign(projectId);
      // keep the last successful summary/result downloadable - a failed run (e.g. a
      // timeout) must not wipe the previous good design
      if (error || !jobId) { window.alert(friendlyRunError(error)); finishWatch(); return; }
      setActiveJobId(jobId);
      watchRef.current = watchDesignJob(jobId, onJobUpdate);
    } catch (e) {
      window.alert(friendlyRunError(e && e.message));
      finishWatch();
    }
  };

  // «لغو»: mark the active job cancelled, then stop watching + clear busy.
  const cancelRun = async () => {
    if (!activeJobId) { finishWatch(); return; }
    await cancelDesignJob(activeJobId);
    finishWatch();
  };

  // Fold a fresh costing into the local summary + the result the KMZ/DDR read, so
  // pricing shows up in the report without a re-run or a project reload.
  const applyCosting = (costing) => {
    setSummary((s) => (s ? { ...s, costing } : s));
    setLastResult((r) => {
      if (!r) return r;
      const next = { ...r, summary: { ...r.summary, costing } };
      if (onResult) onResult(next);
      return next;
    });
  };

  const oltBudgets = [...new Set(((summary && summary.olt_ports) || [])
    .map((o) => Number(o.budget)).filter(Number.isFinite))];
  const sharedOltBudget = oltBudgets.length === 1 ? oltBudgets[0] : null;
  // Spare OLT ports are legal (rules.py); only an over-budget OLT is a defect.
  const oltRows = (summary && summary.olt_ports) || [];
  const oltOver = oltRows.some((o) => Number(o.ports_used) > Number(o.budget));
  const oltSpare = oltRows.reduce((n, o) => n + Math.max(0, Number(o.budget) - Number(o.ports_used) || 0), 0);
  const oltPortStatus = summary && summary.olt_ports_all_exact != null
    ? (summary.olt_ports_all_exact
      ? (sharedOltBudget != null
        ? `همه دقیقاً ${faN(sharedOltBudget)} ✓`
        : 'همهٔ OLTها پورت‌هایشان کامل مصرف شده ✓')
      : (oltOver
        ? 'برخی OLTها بیش از بودجهٔ پورت ✕'
        : `${faN(oltSpare)} پورت آزاد (ذخیرهٔ مجاز)`))
    : '';
  const oltPortTone = summary && summary.olt_ports_all_exact
    ? '#86efac' : (oltOver ? '#fca5a5' : '#cbd5e1');

  return (
    <div style={{ direction: 'rtl', fontFamily: "'Vazirmatn', sans-serif", display: 'flex',
      flexDirection: 'column', gap: 10, padding: 14, borderRadius: 12,
      background: 'linear-gradient(135deg, rgba(15,23,42,0.96), rgba(30,41,59,0.94))', color: '#e2e8f0', minWidth: 260 }}>
      <div style={{ fontWeight: 700, fontSize: 13 }}>طراحی بهینه‌ی شبکه</div>
      {profileName && <div style={{ fontSize: 12, color: '#cbd5e1', marginTop: -4 }}>پیش‌تنظیم: {toFa(profileName)}{profileVersionNo ? ` (نسخهٔ ${toFa(profileVersionNo)})` : ''}</div>}
      <select aria-label="حالت طراحی" value={mode} onChange={(e) => onModeChange(e.target.value)} style={{ padding: 8, borderRadius: 8 }}>
        {MODES.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
      </select>
      <button type="button" onClick={run} disabled={busy} style={{ padding: '9px 14px', borderRadius: 8,
        fontWeight: 700, cursor: busy ? 'wait' : 'pointer', color: '#0b1220',
        background: 'linear-gradient(135deg,#2dd4bf,#0d9488)', border: 'none' }}>
        {busy ? 'در حال طراحی…' : 'اجرای طراحی'}
      </button>
      {busy && progress && (
        <div style={{ fontSize: 12, lineHeight: 1.8, padding: '10px 12px', borderRadius: 10,
          background: 'rgba(13,148,136,0.14)', border: '1px solid rgba(45,212,191,0.32)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 700, color: '#5eead4' }}>
              {stageLabel(progress.stage, progress.status)}
              {progress.detail ? ` - ${toFa(progress.detail)}` : ''}
            </span>
            {progress.total > 0 && (
              <span style={{ color: '#99f6e4', fontVariantNumeric: 'tabular-nums' }}>
                {`«${faN(progress.done)} از ${faN(progress.total)}»`}
              </span>
            )}
          </div>
          <div style={{ height: 7, borderRadius: 5, background: 'rgba(15,23,42,0.55)', overflow: 'hidden', marginTop: 8 }}>
            <div style={{ height: '100%', borderRadius: 5, background: 'linear-gradient(90deg,#2dd4bf,#0d9488)',
              width: progress.total > 0 ? `${Math.min(100, Math.round((progress.done / progress.total) * 100))}%` : '35%',
              transition: 'width .3s ease' }} />
          </div>
          <button type="button" onClick={cancelRun} style={{ marginTop: 10, width: '100%', padding: '7px 12px',
            borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer', color: '#fecaca',
            background: 'rgba(127,29,29,0.45)', border: '1px solid rgba(248,113,113,0.4)' }}>
            لغو
          </button>
        </div>
      )}
      {summary && (
        <div style={{ fontSize: 12, lineHeight: 1.9 }}>
          <div>OLT: {summary.counts.olt} · ODC: {summary.counts.odc}{summary.counts.mfat ? ` · MFAT: ${summary.counts.mfat}` : ''} · FAT: {summary.counts.fat} · HH: {summary.counts.hh}{summary.counts.tc ? ` · TC: ${summary.counts.tc}` : ''}</div>
          {/* Saved designs keep measured drop metres; new designs leave them at zero. */}
          <div>طول کل ترانشه: {summary.total_trench_m} متر{dropsMetered(summary) ? ` · کابل دراپ: ${Math.round(summary.bom.drop_cable_m)} متر` : ''}</div>
          <div style={{ color: summary.errors ? '#fca5a5' : '#86efac' }}>
            {summary.errors ? `${summary.errors} نقض قانون` : 'منطبق با استاندارد ✓'}
          </div>
        </div>
      )}
      {summary && summary.bom && (
        <div style={{ fontSize: 11.5, color: '#cbd5e1', lineHeight: 1.85, borderTop: '1px solid rgba(148,163,184,0.2)', paddingTop: 8 }}>
          <CostBlock summary={summary} projectId={projectId} onPriced={applyCosting} carrier={carrier} canEdit={canEdit} />
          <div>ترانشه: {Object.entries(summary.bom.trench_m_by_layer || {}).map(([k, v]) => `${k} ${v}m`).join(' · ') || '-'}</div>
          <div>{splitterCounts(summary.bom).map(([r, n]) => `اسپلیتر ۱×${faN(r)}: ${faN(n)}`).join(' · ')} · واحدها: {faN(summary.bom.total_units)}</div>
          {summary.bom.cores_active != null && (
            <div>هسته‌ها: فعال {faN(summary.bom.cores_active)} · ذخیره {faN(summary.bom.cores_reserve)}{summary.serviceable_subscribers != null ? ` · ظرفیتِ سرویس‌دهی: ${faN(summary.serviceable_subscribers)}` : ''}</div>
          )}
          {summary.olt_ports_all_exact != null && (
            <div style={{ color: oltPortTone }}>
              پورت‌های OLT: {oltPortStatus}
            </div>
          )}
        </div>
      )}
      {summary && summary.validator && (
        <div style={{ fontSize: 11.5, color: '#94a3b8', lineHeight: 1.85, borderTop: '1px solid rgba(148,163,184,0.2)', paddingTop: 8 }}>
          <div>کفِ تئوری: FAT {summary.validator.fat_count}/{summary.validator.fat_min} ({summary.validator.fat_gap_pct}٪ بالاتر) · ODC {summary.validator.odc_count}/{summary.validator.odc_min} ({summary.validator.odc_gap_pct}٪)</div>
          {summary.ilp && summary.ilp.available && !summary.ilp.skipped && (
            <div style={{ color: '#86efac' }}>
              بهینگیِ FAT (حلِ دقیق): {summary.ilp.heuristic_fats}/{summary.ilp.ilp_fats}
              {' '}({summary.ilp.gap_pct}٪ فاصله{summary.ilp.optimal ? '، کفِ اثبات‌شده' : ''})
            </div>
          )}
          {summary.ilp && summary.ilp.skipped && (
            <div>اعتبارسنجیِ دقیق (ILP): منطقه بزرگ‌تر از حدِ حلِ دقیق است</div>
          )}
        </div>
      )}
      {reloadJob && <div role="alert" style={{ color: '#fca5a5', lineHeight: 1.8 }}>
        بارگذاری نتیجهٔ طرح ناموفق بود؛ دوباره تلاش کنید.
        <button type="button" disabled={busy} onClick={() => {
          setBusy(true); if (onBusyChange) onBusyChange(true);
          onJobUpdate(reloadJob);
        }} style={{ color: '#e2e8f0', background: '#334155', padding: '6px 10px', borderRadius: 8 }}>تلاش دوباره</button>
      </div>}
      {lastResult && (
        <button type="button" onClick={() => downloadDesignKmz(lastResult, exportName, brand)}
          style={{ padding: '8px 12px', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer',
            color: '#e2e8f0', background: 'rgba(51,65,85,0.7)', border: '1px solid rgba(148,163,184,0.32)' }}>
          دانلود KMZ
        </button>
      )}
      {lastResult && (
        <button type="button" onClick={() => downloadText('ftth-DDR.html', buildDesignReport(lastResult, exportName, brand), 'text/html')}
          style={{ padding: '8px 12px', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer',
            color: '#e2e8f0', background: 'rgba(51,65,85,0.7)', border: '1px solid rgba(148,163,184,0.32)' }}>
          دانلود گزارش DDR
        </button>
      )}
      {lastResult && (
        <button type="button" onClick={exportLomLop} disabled={lopExportBusy}
          style={{ padding: '8px 12px', borderRadius: 8, fontWeight: 700, fontSize: 12,
            cursor: lopExportBusy ? 'wait' : 'pointer', color: '#e2e8f0',
            background: 'rgba(51,65,85,0.7)', border: '1px solid rgba(148,163,184,0.32)' }}>
          {lopExportBusy ? 'در حال ساخت فایل اکسل…' : 'دانلود فایل اکسل LOM/LOP'}
        </button>
      )}
      {lastResult && (
        <button type="button" onClick={genReport} disabled={reportBusy}
          style={{ padding: '8px 12px', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: reportBusy ? 'wait' : 'pointer',
            color: '#0b1220', background: 'linear-gradient(135deg,#2dd4bf,#0d9488)', border: 'none' }}>
          {reportBusy ? 'در حال نوشتن گزارش…' : 'گزارشِ هوشمندِ طراحی'}
        </button>
      )}
      {errors.map((v, i) => (
        <div key={i} style={{ fontSize: 11.5, color: '#fca5a5' }}>• {v.message}</div>
      ))}

      {smartReport && (
        <div onClick={() => setSmartReport(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(2,6,23,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ direction: 'rtl', width: 'min(640px, 96vw)', maxHeight: '82vh', overflowY: 'auto',
              background: 'linear-gradient(135deg, rgba(15,23,42,0.98), rgba(30,41,59,0.97))',
              border: '1px solid rgba(148,163,184,0.24)', borderRadius: 16, padding: '18px 20px',
              color: '#e2e8f0', boxShadow: '0 24px 60px rgba(2,6,23,0.7)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontWeight: 800, fontSize: 15, color: '#f1f5f9' }}>گزارشِ هوشمندِ طراحی</div>
              <button type="button" onClick={() => setSmartReport(null)} aria-label="بستن"
                style={{ width: 28, height: 26, borderRadius: 8, cursor: 'pointer', color: '#cbd5e1', fontSize: 15,
                  background: 'rgba(51,65,85,0.7)', border: '1px solid rgba(148,163,184,0.3)' }}>×</button>
            </div>
            <div style={{ whiteSpace: 'pre-wrap', lineHeight: 2, fontSize: 13.5 }}>{smartReport}</div>
          </div>
        </div>
      )}
    </div>
  );
}
